import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { View } from 'react-native'
import { useSelector } from 'react-redux'

import styled from 'styled-components/native'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { ProviderChatThreadList } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/providerChatThreadList/ProviderChatThreadList'

import BaseChat, { ChatEntryPoint } from './BaseChat'
import { ClientProfileMessagesPopover } from './ClientProfileMessagesPopover'
import { useEnsureConversationForThread } from './data/hooks/useEnsureConversationForThread'
import { useMessengerThreads } from './data/hooks/useMessengerThreads'
import { useProviderChatThreadRows } from './data/hooks/useProviderChatThreadRows'
import { useProviderPairMessagingAccess } from './data/hooks/useProviderPairMessagingAccess'
import { useSelectMessengerThread } from './data/hooks/useSelectMessengerThread'
import {
  getClientProfileMessengerTarget,
  getMessagesActiveLiveMsgAppt,
  getSelectedConversationId,
  inLiveMsgSession,
} from './data/messagesSelectors'
import {
  clearSelectedConversation,
  setClientProfileMessengerTarget,
  setSelectedConversation,
} from './data/messagesSlice'
import { MessengerParticipant } from './data/types'
import { useChatEntryPointFromRoute } from './hooks/useChatEntryPointFromRoute'
import { useClientMessenger } from './hooks/useClientMessenger'
import styles from './messagesPopover.module.scss'
import { getClientFullName } from '../../common/utils/utils'
import { getSelectedClientId } from '../../data/lyraTherapy/clientSelectors'
import { useAppDispatch } from '../../data/store'
import { useGuardianForSelectedClient } from '../../hooks/useGuardianForSelectedClient'
import { useSelectedClientData } from '../clients/clientDetails/data/hooks/useSelectedClientData'

export type MessagePopoverProps = {
  closePopover: () => void
  className?: string
  messagesPopoverContainer?: React.RefObject<HTMLElement>
  selectedEpisodeId?: string
  entryPointOverride?: ChatEntryPoint
  hideThreadList?: boolean
  initialParticipant?: MessengerParticipant
}

const ThreadListPane = styled(View)(({ theme }) => ({
  width: 338,
  borderRightWidth: 1,
  borderRightColor: theme.colors.borderDefault,
  backgroundColor: theme.colors.backgroundPrimary,
}))

const TwoPaneRow = styled(View)({
  flexDirection: 'row',
  flex: 1,
})

const LegacyMessagesPopover: FunctionComponent<MessagePopoverProps> = ({
  closePopover,
  className,
  messagesPopoverContainer,
  selectedEpisodeId,
  entryPointOverride,
  hideThreadList,
  initialParticipant = MessengerParticipant.PATIENT,
}) => {
  const { formatMessage } = useIntl()
  const inLiveSession = useSelector(inLiveMsgSession)
  const initialConversationId = useSelector(getSelectedConversationId)
  const messageContainer = useRef(null)
  const { isPreferredNameEnabled } = useFlags()
  const { data: client } = useSelectedClientData()
  let entryPoint = useChatEntryPointFromRoute()
  entryPoint = entryPointOverride || entryPoint

  const { guardian, isGuardianForMinor } = useGuardianForSelectedClient()
  const patientFullName = client ? getClientFullName(client, isPreferredNameEnabled) : ''

  const dispatch = useAppDispatch()
  const { threads, isLoading: isLoadingThreads, threadsLoaded } = useMessengerThreads()
  const exactInitialThread = initialConversationId
    ? threads.find((thread) => thread.conversationId === initialConversationId)
    : undefined
  const isExactThreadPending = Boolean(hideThreadList && initialConversationId && !exactInitialThread)
  const popoverThreads = useMemo(() => {
    if (!hideThreadList) {
      return threads.filter((thread) => thread.participant !== MessengerParticipant.AI)
    }
    if (initialConversationId) {
      return exactInitialThread ? [exactInitialThread] : []
    }
    return threads.filter((thread) => thread.participant === initialParticipant)
  }, [threads, hideThreadList, initialConversationId, exactInitialThread, initialParticipant])
  const hasGuardianThread = useMemo(
    () => !hideThreadList && popoverThreads.some((thread) => thread.participant === MessengerParticipant.GUARDIAN),
    [popoverThreads, hideThreadList],
  )
  const { selectedThreadId, selectedParticipant, selectThread, selectedConversationId, selectedThread } =
    useSelectMessengerThread(popoverThreads, initialParticipant, hideThreadList ? initialConversationId : undefined)
  const threadRows = useProviderChatThreadRows(popoverThreads)
  const fallbackThread = !hideThreadList && !isLoadingThreads && !selectedThread ? popoverThreads[0] : undefined

  const ensureSelectedConversation = useEnsureConversationForThread(selectedThread, threadsLoaded)

  // A caregiver can become ineligible while the popover is open. Move to the first remaining
  // thread instead of retaining an id that no longer has a corresponding conversation.
  useEffect(() => {
    if (selectedThreadId && fallbackThread) {
      selectThread(fallbackThread.id)
    }
  }, [fallbackThread, selectThread, selectedThreadId])

  // Sync the resolved thread conversation id into Redux so BaseChat follows thread selection.
  // Skip the sync while threads are loading or an exact entry-point target has not arrived yet.
  // Otherwise a same-participant fallback could overwrite the caller's requested conversation.
  useEffect(() => {
    if (isLoadingThreads || isExactThreadPending || fallbackThread) {
      return
    }
    if (selectedConversationId) {
      dispatch(setSelectedConversation(selectedConversationId))
    } else {
      dispatch(clearSelectedConversation())
    }
  }, [dispatch, selectedConversationId, isLoadingThreads, isExactThreadPending, fallbackThread])

  const teenRoleLabel = formatMessage({
    defaultMessage: 'Teen',
    description: 'Popover chat header role subtitle for the teen client',
  })
  const caregiverRoleLabel = formatMessage({
    defaultMessage: 'Caregiver',
    description: 'Popover chat header role subtitle for the caregiver',
  })

  const { chatHeaderTitle, chatHeaderSubTitleMsg } = useMemo(() => {
    if (inLiveSession) {
      return {
        chatHeaderTitle: formatMessage({
          defaultMessage: 'In-session messaging',
          description: 'Chat header title during a live messaging session',
        }),
        chatHeaderSubTitleMsg: undefined as string | undefined,
      }
    }
    if (hasGuardianThread || selectedParticipant === MessengerParticipant.GUARDIAN) {
      const isGuardianSelected = selectedParticipant === MessengerParticipant.GUARDIAN
      return {
        chatHeaderTitle: isGuardianSelected ? guardian.guardianName : patientFullName,
        chatHeaderSubTitleMsg: isGuardianSelected ? caregiverRoleLabel : teenRoleLabel,
      }
    }
    if (isGuardianForMinor) {
      return {
        chatHeaderTitle: formatMessage(
          {
            defaultMessage: "{guardianName} ({patientFullName}'s caregiver)",
            description: 'Chat header showing guardian name with patient relationship',
          },
          { guardianName: guardian.guardianName, patientFullName },
        ),
        chatHeaderSubTitleMsg: undefined,
      }
    }
    return {
      chatHeaderTitle:
        (isPreferredNameEnabled ? (client?.preferred_first_name ?? client?.first_name) : client?.first_name) ?? '',
      chatHeaderSubTitleMsg: undefined,
    }
  }, [
    inLiveSession,
    hasGuardianThread,
    selectedParticipant,
    guardian.guardianName,
    patientFullName,
    isGuardianForMinor,
    isPreferredNameEnabled,
    client,
    formatMessage,
    teenRoleLabel,
    caregiverRoleLabel,
  ])

  const handleOutsideClick = useCallback(
    (e) => {
      e.stopPropagation()
      if (messageContainer.current) {
        const chatBoundingRectangle = (messageContainer.current as HTMLElement).children[0].getBoundingClientRect()
        const isTargetInsideMessageContainer =
          e.clientX > chatBoundingRectangle.left &&
          e.clientX < chatBoundingRectangle.right &&
          e.clientY > chatBoundingRectangle.top &&
          e.clientY < chatBoundingRectangle.bottom

        const isClickOnMessagePopoverContainer = messagesPopoverContainer?.current?.contains(e.target)
        if (!isTargetInsideMessageContainer && !isClickOnMessagePopoverContainer) {
          closePopover()
        }
      }
    },
    [closePopover, messagesPopoverContainer],
  )

  useEffect(() => {
    window.addEventListener('mousedown', handleOutsideClick, false)
    return () => {
      window.removeEventListener('mousedown', handleOutsideClick, false)
    }
  }, [handleOutsideClick])

  return (
    <div
      className={`popover-container ${styles['popover-container']} ${
        className?.includes('messaging-hub-popover') ? styles['messaging-hub-popover'] : ''
      } ${hasGuardianThread ? styles['two-pane'] : ''}`}
      ref={messageContainer}
    >
      {hasGuardianThread ? (
        <TwoPaneRow>
          <ThreadListPane>
            <ProviderChatThreadList threads={threadRows} selectedId={selectedThreadId} onThreadPress={selectThread} />
          </ThreadListPane>
          <BaseChat
            chatHeaderTitle={chatHeaderTitle}
            chatHeaderSubTitleMsg={chatHeaderSubTitleMsg}
            liveSessionBadge={inLiveSession ? 'LIVE SESSION' : ''}
            closePopover={closePopover}
            entryPoint={entryPoint}
            selectedEpisodeId={selectedEpisodeId}
            recipientName={selectedThread?.recipientName}
            recipientId={selectedThread?.recipientId}
            ensureConversation={ensureSelectedConversation}
            receiverMsgable={selectedThread?.canSendMessages ?? true}
            showInputActions={selectedThread?.showInputActions ?? true}
            disableDrafts={selectedThread?.disableDrafts ?? false}
            patientFullName={patientFullName}
            guardianName={guardian.guardianName}
          />
        </TwoPaneRow>
      ) : (
        <BaseChat
          chatHeaderTitle={chatHeaderTitle}
          liveSessionBadge={inLiveSession ? 'LIVE SESSION' : ''}
          closePopover={closePopover}
          entryPoint={entryPoint}
          selectedEpisodeId={selectedEpisodeId}
          recipientId={selectedThread?.recipientId}
          ensureConversation={ensureSelectedConversation}
        />
      )}
    </div>
  )
}

export const MessagesPopover: FunctionComponent<MessagePopoverProps> = (props) => {
  const isActiveMessenger = useClientMessenger(props.closePopover)
  const dispatch = useAppDispatch()
  const clientProfileMessengerTarget = useSelector(getClientProfileMessengerTarget)
  // A Care services target applies only to the opening it was set for, including one shown as a live chat.
  // Clear only the target this popover saw, so closing it never clears one set for the next opening.
  useEffect(
    () => () => {
      dispatch((dispatch, getState) => {
        if (getClientProfileMessengerTarget(getState()) === clientProfileMessengerTarget) {
          dispatch(setClientProfileMessengerTarget(null))
        }
      })
    },
    [clientProfileMessengerTarget, dispatch],
  )
  const { canAccessProviderPairs } = useProviderPairMessagingAccess()
  const inLiveSession = useSelector(inLiveMsgSession)

  const activeLiveMsgAppt = useSelector(getMessagesActiveLiveMsgAppt)
  const selectedClientId = useSelector(getSelectedClientId)
  // The opening effect sets inLiveSession after child effects. Choose the live chat
  // immediately for this appointment's client so the shared messenger never mounts.
  const isLiveSessionClient = !!activeLiveMsgAppt && activeLiveMsgAppt.userId === selectedClientId
  // A Care services provider opens their conversation even during this client's live session.
  const opensProviderConversation = clientProfileMessengerTarget?.clientId === selectedClientId

  if (!isActiveMessenger) return null

  if (
    canAccessProviderPairs &&
    !props.hideThreadList &&
    (opensProviderConversation || (!inLiveSession && !isLiveSessionClient))
  ) {
    return <ClientProfileMessagesPopover {...props} />
  }

  return <LegacyMessagesPopover {...props} />
}

export default MessagesPopover
